import { faEdit } from "@fortawesome/pro-regular-svg-icons";
import { faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";

import { Card } from "src/base-components/Card";
import { StatusIcon } from "src/base-components/StatusIcon";
import {
  Agent,
  AgentVersion,
  AgentVersionStatus,
} from "src/clients/agent-management";
import { Button } from "src/design-system/Button";
import { IconLink } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { UserNameInfo } from "src/flowContainer/versionEditedInfo/UsernameInfo";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { getBaseUrl, getUrlToAgentVersionPage } from "src/router/urls";
import { formatDate } from "src/utils/datetime";

type Props = {
  agent: Agent;
  version: AgentVersion;
  immutable: boolean;
  handleChangeAgent: () => void;
};

const LastEdited: React.FC<{ version: AgentVersion }> = ({ version }) => {
  const userId =
    version.metadata.updated_by_id || version.metadata.created_by_id;

  return (
    <div className="flex flex-row items-center gap-x-1">
      <p className="font-inter-normal-12px text-gray-600">Edited by</p>
      <div className="flex items-center gap-x-1 font-inter-medium-12px text-gray-800">
        <UserNameInfo userId={userId} withAvatar />
      </div>
      <p className="font-inter-normal-12px text-gray-600">on</p>
      <p className="font-inter-medium-12px text-gray-800">
        {formatDate(version.metadata.updated_at)}
      </p>
    </div>
  );
};

const StatusIndicator: React.FC<{ status: AgentVersionStatus }> = ({
  status,
}) => {
  const isPublished = status === AgentVersionStatus.PUBLISHED;
  return (
    <StatusIcon variant={isPublished ? "published" : "draft"} size="2xs" />
  );
};

export const SelectedAgentDisplay: React.FC<Props> = ({
  agent,
  version,
  handleChangeAgent,
  immutable,
}) => {
  const { orgId, workspace } = useAuthoringContext();
  const agentVersionUrl = `${getBaseUrl()}${getUrlToAgentVersionPage(
    orgId,
    workspace.id,
    agent.id,
    version.id,
  )}`;

  return (
    <Card>
      <Card.Header>
        <div className="flex flex-row items-center gap-x-1">
          <StatusIndicator status={version.status} />
          <p
            className="font-inter-semibold-14px text-gray-800"
            data-loc="agent-selected-version-display-header"
          >
            {agent.name}, {version.name}
          </p>
        </div>

        <Tooltip placement="bottom-end" title="Open in new tab" asChild>
          <IconLink
            aria-label="Open in new tab"
            href={agentVersionUrl}
            icon={faUpRightFromSquare}
            openInNewTab
            size="xs"
          />
        </Tooltip>
      </Card.Header>
      <div className="space-y-1 pt-1">
        <p className="whitespace-pre-line font-inter-normal-12px text-gray-500">
          {agent.description}
        </p>
        <LastEdited version={version} />
      </div>
      <div className="mt-3">
        <Button
          isDisabled={immutable}
          iconLeft={faEdit}
          size="sm"
          variant="secondary"
          onClick={handleChangeAgent}
        >
          Change Agent
        </Button>
      </div>
    </Card>
  );
};
